import { useEffect, useState } from 'react'
import type { MouvementRenomDTO, ReputationDTO } from '@whatremains/shared'
import { api } from '../api'

/**
 * Le renom.
 *
 * Affiché en clair, avec sa valeur et les bornes du palier courant. C'est un
 * contraste délibéré avec les artefacts : le monde te dit toujours où tu en es
 * avec lui, eux seuls se taisent.
 *
 * On n'affiche jamais la matrice d'antagonisme. Qu'aider l'un coûte auprès de
 * l'autre se constate, ça ne s'annonce pas.
 *
 * Se constate, donc : le registre des mouvements est en tête d'écran, et c'est
 * lui qu'Enzo réclamait, deux fois. « Je sais toujours pas comment prendre ou
 * perdre de la réputation. » Le palier disait où l'on en est, jamais ce qui y
 * a mené. Les répercussions y sont marquées comme telles, sans quoi une chute
 * chez des gens qu'on n'a jamais croisés reste une énigme.
 */
/** « il y a 3 h », sans bibliothèque et sans faux précis. */
/** Combien d'ÉVÉNEMENTS du registre s'affichent d'emblée. Le reste se déplie. */
const VISIBLES = 15

/**
 * Le registre, groupé par ce qui l'a causé.
 *
 * Enzo, après une quête : « j'ai fait un choix qui devait SERVIR les
 * Non-inscrits et j'ai gagné La Créance, gagné les Demeurants, perdu les Sourds
 * et les Non-inscrits. Il y a donc un souci. »
 *
 * Vérification faite en base : il avait **gagné +30 net** chez les
 * Non-inscrits. Une seule quête produit jusqu'à NEUF lignes — l'effet direct du
 * choix, ses répercussions, puis la récompense de fin et SES répercussions — et
 * le journal les listait à plat, du plus récent au plus ancien. En tête il
 * lisait donc les répercussions de la récompense, qui disent l'inverse de son
 * choix. Il a lu la moitié qui le contredit.
 *
 * On groupe donc par cause, et on montre le NET par faction. Le détail reste
 * accessible ligne à ligne : c'est le résumé qui manquait, pas les lignes.
 */
interface Evenement {
  cause: string
  ilYAMinutes: number
  /** Le net par faction, dans l'ordre du plus fort mouvement. */
  parFaction: { faction: string; valeur: number }[]
}

function grouper(mouvements: MouvementRenomDTO[]): Evenement[] {
  const par = new Map<string, Evenement>()
  for (const m of mouvements) {
    let e = par.get(m.cause)
    if (!e) {
      e = { cause: m.cause, ilYAMinutes: m.ilYAMinutes, parFaction: [] }
      par.set(m.cause, e)
    }
    // Le plus récent des mouvements de la cause donne son âge.
    e.ilYAMinutes = Math.min(e.ilYAMinutes, m.ilYAMinutes)
    const f = e.parFaction.find((x) => x.faction === m.faction)
    if (f) f.valeur += m.valeur
    else e.parFaction.push({ faction: m.faction, valeur: m.valeur })
  }
  for (const e of par.values()) {
    // Ce qui a le plus bougé se lit en premier, et ce qui n'a pas bougé du tout
    // ne se lit pas : un « ±0 » n'apprend rien et allonge la ligne.
    e.parFaction = e.parFaction
      .filter((f) => f.valeur !== 0)
      .sort((a, b) => Math.abs(b.valeur) - Math.abs(a.valeur))
  }
  return [...par.values()].filter((e) => e.parFaction.length > 0)
}

function depuis(minutes: number): string {
  if (minutes < 1) return 'à l’instant'
  if (minutes < 60) return `il y a ${minutes} min`
  const h = Math.round(minutes / 60)
  if (h < 24) return `il y a ${h} h`
  const j = Math.round(h / 24)
  return j === 1 ? 'hier' : `il y a ${j} jours`
}

export function Renom({ personnageId }: { personnageId: string }) {
  const [factions, setFactions] = useState<ReputationDTO[] | null>(null)
  const [mouvements, setMouvements] = useState<MouvementRenomDTO[]>([])

  useEffect(() => {
    void api.reputations(personnageId).then(setFactions)
    void api.mouvementsRenom(personnageId).then(setMouvements).catch(() => setMouvements([]))
  }, [personnageId])

  if (!factions) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  const evenements = grouper(mouvements)

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">Ce qu’on pense de toi</div>
        <h1>Renom</h1>
        {/*
          Le principe, dit une fois et toujours visible.

          Il ne l'était que dans l'état vide, c'est-à-dire au seul moment où le
          joueur n'avait encore rien fait qui puisse l'illustrer. Enzo a posé
          deux fois la question « comment je prends ou je perds de la
          réputation », et une troisième fois autrement : « ce n'est pas clair
          quelle faction fait quoi ».

          La règle s'énonce, la matrice ne se publie pas : de combien, et chez
          qui exactement, se constatent dans le registre ci-dessous.
        */}
        <p className="attenue" style={{ maxWidth: '62ch' }}>
          Cinq puissances, et elles ne veulent pas la même chose. Le renom se gagne en
          menant des quêtes à bout, en relevant des besognes et en traitant avec les
          gens ; servir les uns coûte auprès de ceux qu’ils n’aiment pas, et l’on ne
          peut donc pas être bien vu partout.
        </p>
      </div>

      {factions.map((f) => {
        const etendue = f.palierMax - f.palierMin
        const part = etendue > 0 ? ((f.valeur - f.palierMin) / etendue) * 100 : 0
        return (
          <section key={f.code}>
            <div className="surtitre">{f.palier}</div>
            {/* Le métier accolé au nom, ici comme partout où le nom paraît. */}
            <h2 style={{ marginBottom: 10 }}>
              {f.nom} <span className="tenu" style={{ fontSize: 14 }}>· {f.metier}</span>
            </h2>
            <p className="attenue" style={{ marginBottom: 6 }}>{f.nature}</p>
            <p className="tenu" style={{ fontSize: 13 }}>Veut : {f.veut}</p>

            <div className="jauge" style={{ maxWidth: 420, marginTop: 14 }}>
              <div style={{ width: `${Math.max(2, Math.min(100, part))}%` }} />
            </div>
            <p className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11, marginTop: 8 }}>
              {f.valeur}   ·   {f.palierMin} à {f.palierMax}
            </p>
          </section>
        )
      })}

      {/*
        Le registre, SOUS les scores.

        Enzo : « il faut que sur la partie renom tu passes le log sous les
        scores de renommée, parce que là, surtout sur téléphone, je dois tout
        balayer pour arriver enfin à l'info que je veux. »

        Il avait raison sur l'ordre de lecture : on vient ici pour savoir où
        l'on en est, et le journal explique ensuite POURQUOI. Mis avant, il
        obligeait à traverser soixante lignes pour atteindre cinq chiffres.

        Et il est borné à trente. Le serveur en envoie soixante ; le reste se
        déplie, il ne se jette pas — savoir ce qui a coûté cher il y a deux
        jours est parfois précisément ce qu'on cherche.
      */}
      <section>
        <div className="surtitre">Ce qui l’a fait bouger</div>
        {evenements.length === 0 ? (
          <p className="tenu">Rien encore.</p>
        ) : (
          <>
            <ul className="evenements">
              {evenements.slice(0, VISIBLES).map((e, i) => (
                <li key={i}>
                  <span className="evenement-cause">{e.cause}</span>
                  <span className="evenement-quand">{depuis(e.ilYAMinutes)}</span>
                  <span className="evenement-bilan">
                    {e.parFaction.map((f) => (
                      <span key={f.faction} className="bilan-faction">
                        <b className={f.valeur > 0 ? 'gain' : 'perte'}>
                          {f.valeur > 0 ? `+${f.valeur}` : f.valeur}
                        </b>
                        {f.faction}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
            {evenements.length > VISIBLES && (
              <details className="repli">
                <summary>
                  Plus ancien
                  <span className="tenu"> · {evenements.length - VISIBLES}</span>
                </summary>
                <ul className="evenements">
                  {evenements.slice(VISIBLES).map((e, i) => (
                    <li key={VISIBLES + i}>
                      <span className="evenement-cause">{e.cause}</span>
                      <span className="evenement-quand">{depuis(e.ilYAMinutes)}</span>
                      <span className="evenement-bilan">
                        {e.parFaction.map((f) => (
                          <span key={f.faction} className="bilan-faction">
                            <b className={f.valeur > 0 ? 'gain' : 'perte'}>
                              {f.valeur > 0 ? `+${f.valeur}` : f.valeur}
                            </b>
                            {f.faction}
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </section>
    </div>
  )
}
